import React from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { FaFacebookF, FaLinkedinIn } from 'react-icons/fa';
import BrandLogo from './BrandLogo';
import BrandName from './BrandName';
import mail from '../assets/images/mail.png';
import phone1 from '../assets/images/phone1.png';
import location from '../assets/images/location.png';
import { gradients, colors, containImageSx } from '../theme/designTokens';
import { footerLinks } from '../data/navigation';

const Footer = () => {
  return (
    <Box
      component="footer"
      sx={{
        width: '100%',
        background: gradients.footer,
        padding: { xs: '3.5rem 1.25rem', sm: '4rem 2rem', md: '5rem 4rem' },
        color: '#fff',
        borderTop: `1px solid ${colors.border}`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: '1fr 1fr',
            lg: '1.5fr 1fr 1.2fr 0.8fr',
          },
          gap: { xs: 4, sm: 4, md: 5 },
          marginBottom: '2.5rem',
          position: 'relative',
        }}
      >
        <Box sx={{ maxWidth: { xs: '100%', lg: 360 } }}>
          <BrandLogo
            component={motion.div}
            whileHover={{ scale: 1.02 }}
            imageSx={{
              maxWidth: '220px',
              width: '100%',
              height: 'auto',
              mb: 2,
            }}
            sx={{ mb: 2 }}
          />
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.72)',
              fontSize: { xs: '0.9rem', md: '0.95rem' },
              lineHeight: 1.75,
              mb: 1,
            }}
          >
            Enterprise Resilience Advisory & Engineering. We design resilience architectures where insurance becomes one component of an integrated protection strategy.
          </Typography>
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 2, color: colors.ochre, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Navigate
          </Typography>
          <Box component="nav" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {footerLinks.map(({ label, to }) => (
              <Box
                key={to}
                component={RouterLink}
                to={to}
                sx={{
                  color: 'rgba(255,255,255,0.75)',
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  transition: 'all 0.25s ease',
                  '&:hover': { color: colors.white, transform: 'translateX(4px)' },
                }}
              >
                {label}
              </Box>
            ))}
          </Box>
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 2, color: colors.ochre, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Connect
          </Typography>
          {[
            { icon: mail, alt: 'Email', text: 'info@alegion.net' },
            { icon: phone1, alt: 'Phone', text: '+91 95000 82260' },
            {
              icon: location,
              alt: 'Location',
              text: 'New No. 7C, Perumal Koil Street, Nerkundram, Chennai, Tamil Nadu - 600107',
            },
          ].map(({ icon, alt, text }) => (
            <Typography
              key={alt}
              sx={{ fontSize: '0.9rem', lineHeight: 1.7, mb: 1.5, color: 'rgba(255,255,255,0.75)' }}
            >
              <Box
                component="img"
                src={icon}
                alt={alt}
                sx={{
                  ...containImageSx,
                  width: 18,
                  height: 18,
                  mr: 1,
                  display: 'inline',
                  verticalAlign: 'middle',
                  filter: 'brightness(0) invert(1)',
                  opacity: 0.75,
                }}
              />
              {text}
            </Typography>
          ))}
        </Box>

        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', mb: 2, color: colors.ochre, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Follow
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {[
              {
                href: 'https://www.facebook.com/people/ALEgION-Insurance-Broking-Pvt-Ltd/100070054664640/',
                Icon: FaFacebookF,
                alt: 'Facebook',
              },
              {
                href: 'https://www.linkedin.com/company/alegion-insurance-broking-ltd./about/',
                Icon: FaLinkedinIn,
                alt: 'LinkedIn',
              },
            ].map(({ href, Icon, alt }) => (
              <Box
                key={alt}
                component={motion.a}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={alt}
                whileHover={{ y: -2 }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  border: '1.5px solid rgba(255,255,255,0.55)',
                  color: colors.white,
                  fontSize: '1rem',
                  textDecoration: 'none',
                  transition: 'background 0.25s ease, border-color 0.25s ease',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.1)',
                    borderColor: colors.white,
                  },
                }}
              >
                <Icon />
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1,
          borderTop: '1px solid rgba(255,255,255,0.1)',
          paddingTop: '1.5rem',
          fontSize: '0.8rem',
          color: 'rgba(255,255,255,0.45)',
        }}
      >
        <span>© 2026 <BrandName />. All Rights Reserved.</span>
        <span>AERA™ — <BrandName /> Enterprise Resilience Architecture</span>
      </Box>
    </Box>
  );
};

export default Footer;
